import React, { useState, useEffect } from 'react';
import {
  X,
  RotateCcw,
  Sun,
  AlignJustify,
  Link as LinkIcon,
  FileText,
  ArrowRight,
} from 'lucide-react';
import type { AccessibilitySettings } from '../types/accessibility';
import {
  getAccessibilitySettings,
  saveAccessibilitySettings,
  resetAccessibilitySettings,
  applyAccessibilitySettings,
} from '../utils/accessibility';

interface AccessibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AccessibilityModal: React.FC<AccessibilityModalProps> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(getAccessibilitySettings());

  useEffect(() => {
    if (isOpen) {
      const current = getAccessibilitySettings();
      setSettings(current);
      applyAccessibilitySettings(current);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUpdate = (newSettings: Partial<AccessibilitySettings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    saveAccessibilitySettings(updated);
  };

  const handleReset = () => {
    const reset = resetAccessibilitySettings();
    setSettings(reset);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 max-w-md w-full space-y-6 shadow-2xl relative text-slate-900 dark:text-white">
        {/* Üst Başlık & Kapat Butonu */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
              Erişilebilirlik
            </h2>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              Ayarlar tarayıcınızda saklanır
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* 1. YAZI BOYUTU (FONT SIZE) */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-900 dark:text-white block">
            Yazı Boyutu
          </label>
          <div className="grid grid-cols-4 gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl text-center">
            {[
              { label: 'Normal', scale: '100%', val: 100 },
              { label: 'Büyük', scale: '115%', val: 115 },
              { label: 'Daha Büyük', scale: '130%', val: 130 },
              { label: 'En Büyük', scale: '150%', val: 150 },
            ].map((item) => {
              const active = settings.fontSize === item.val;
              return (
                <button
                  key={item.val}
                  type="button"
                  onClick={() => handleUpdate({ fontSize: item.val as AccessibilitySettings['fontSize'] })}
                  className={`py-2 px-1 rounded-xl transition-all flex flex-col items-center justify-center ${
                    active
                      ? 'bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 font-bold shadow-sm'
                      : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 font-medium'
                  }`}
                >
                  <span className="text-xs">{item.label}</span>
                  <span className="text-[10px] opacity-75">{item.scale}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. YÜKSEK KONTRAST */}
        <div className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-950/60 text-amber-600 rounded-xl flex items-center justify-center shrink-0">
              <Sun size={20} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">Yüksek Kontrast</h4>
              <p className="text-[11px] text-slate-400">Sarı-siyah renk şeması</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.highContrast}
              onChange={(e) => handleUpdate({ highContrast: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>

        {/* 3. GENİŞ SATIR ARALIGI */}
        <div className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-950/60 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <AlignJustify size={20} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">Geniş Satır Aralığı</h4>
              <p className="text-[11px] text-slate-400">Okumayı kolaylaştırır</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.wideLineSpacing}
              onChange={(e) => handleUpdate({ wideLineSpacing: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>

        {/* 4. BAĞLANTILARI VURGULA */}
        <div className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-950/60 text-purple-600 rounded-xl flex items-center justify-center shrink-0">
              <LinkIcon size={20} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">Bağlantıları Vurgula</h4>
              <p className="text-[11px] text-slate-400">Tüm bağlantıları altı çizili göster</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.highlightLinks}
              onChange={(e) => handleUpdate({ highlightLinks: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>

        {/* 5. KOLAY OKUMA */}
        <div className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 rounded-xl flex items-center justify-center shrink-0">
              <FileText size={20} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">Kolay Okuma</h4>
              <p className="text-[11px] text-slate-400">Geniş boşluklar, sade düzen</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.easyReading}
              onChange={(e) => handleUpdate({ easyReading: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>

        {/* ALT BAR (Sıfırla & Beyan) */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <RotateCcw size={14} /> Ayarları sıfırla
          </button>

          <a
            href="https://bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400 hover:underline"
          >
            Erişilebilirlik beyanı <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};
